import type {
	AiJobRequest,
	BillingUpgradeRequest,
	CheckoutRequest,
	CheckoutResponse,
	ForgotPasswordRequest,
	ChangePasswordRequest,
	CompleteOAuthLinkRequest,
	EmailVerificationRecoveryStatusResponse,
	LoginRequest,
	PlanCode,
	PlaybackTokenRequest,
	RegisterRequest,
	RegisterResponse,
	ResendEmailVerificationRequest,
	ResendEmailVerificationResponse,
	ResetPasswordRequest,
	ResetPasswordResponse,
	ResetPasswordValidationRequest,
	ResetPasswordValidationResponse,
	TopicChapterListResponse,
	UpgradePreviewRequest,
	VerifyEmailRequest,
} from "./apiContracts";
import type { PublicIndexResponse } from "./apiTopicContracts";
import { PrototypeApiError, PrototypeApiTransport, type TransportConfig } from "./apiTransport";
import { recordTransportAttemptFailure } from "./clientSessionDiagnostics";
import { pagedQuery, type PagedListOptions, type TopicCatalogOptions, type TopicNewestOptions, type TopicTrendingOptions } from "./apiPaging";
import { parseBillingUpgradePreviewResponse, type AiJobDetailResponse, type BillingUpgradePreviewResponse, type CommunityIdeaDetailResponse, type SiteMetricsResponse, type TopicListResponse, type ViewerEnvelope } from "./apiClientResponses";
import type { ChapterPageModel, TopicPageModel } from '../../shared/pages/pageModels';
import { acceptPlaybackAuthorization, resetPlaybackAuthorizationContext, settlePlaybackTokenRequest, withPlaybackAuthorization } from './playbackAuthorizationContext';

const DEEPMOX_TRANSPORT_CONFIG: TransportConfig = {
	errorLabel: "DeepMox API",
	anonymousIdStorageKey: "deepmox-anonymous-id",
	anonymousIdHeader: "x-deepmox-anonymous-id",
	globalCallsKey: "__deepmoxApiCalls",
	onRequestAttemptFailed: recordTransportAttemptFailure,
};

type CurrentSubscriptionResponse = {
	subscription: {
		id: string;
		providerSubscriptionId: string;
		plan: string;
		billingCycle: string;
		status: string;
		cancelAtPeriodEnd: boolean;
		currentPeriodStart: string | null;
		currentPeriodEnd: string | null;
		providerCustomerId: string | null;
	} | null;
	coverage: { subscriptionId: string; providerSubscriptionId: string | null; plan: string; status: string; providerStatus: string | null; verifiedPaidThrough: string | null; blocked: boolean; blockReason: string | null } | null;
	changes?: Array<{
		requestId: string | null;
		changeType: string;
		status: string;
		toPlan: string | null;
		toBillingCycle: string | null;
		reasonCode: string | null;
	}>;
};

/** Authentication, catalog, billing, playback, and AI-create endpoint facade. */
export class PrototypeContentApiClient extends PrototypeApiTransport {
	private currentSubscriptionRequest: { promise: Promise<CurrentSubscriptionResponse>; settledAt: number | null } | null = null;
	protected onIdentityScopeChanged(): void { resetPlaybackAuthorizationContext(); }

	constructor(baseUrl = "") { super(baseUrl, DEEPMOX_TRANSPORT_CONFIG); }

	bootstrap(plan: PlanCode) { return this.get(`/api/bootstrap?plan=${encodeURIComponent(plan)}&lean=1`) as Promise<ViewerEnvelope>; }
	plans() { return this.get('/api/plans') as Promise<{ plans: unknown; catalogLoadedAt: string; catalogExpiresAt: string }>; }
	getSession() { return this.get("/api/auth/session") as Promise<ViewerEnvelope | null>; }
	publicConfig() { return this.get("/api/public-config") as Promise<{ turnstileSiteKey: string | null; environment: string }>; }
	forgotPassword(input: ForgotPasswordRequest) { return this.post("/api/auth/forgot-password", input); }
	verifyEmail(input: VerifyEmailRequest) { return this.post("/api/auth/verify-email", input); }
	resendEmailVerification(input: ResendEmailVerificationRequest) {
		return this.post('/api/auth/resend-verification', input) as Promise<ResendEmailVerificationResponse>;
	}
	emailVerificationRecoveryStatus() {
		return this.get('/api/auth/email-verification-recovery/status') as Promise<EmailVerificationRecoveryStatusResponse>;
	}
	requestEmailVerificationRecovery(turnstileToken: string) {
		return this.post('/api/auth/email-verification-recovery/resend', { turnstileToken }) as Promise<Pick<EmailVerificationRecoveryStatusResponse, 'status'>>;
	}
	resetPassword(input: ResetPasswordRequest) {
		return this.post('/api/auth/reset-password', input) as Promise<ResetPasswordResponse>;
	}
	validateResetPasswordToken(input: ResetPasswordValidationRequest) {
		return this.post('/api/auth/reset-password/validate', input) as Promise<ResetPasswordValidationResponse>;
	}
	changePassword(input: ChangePasswordRequest) { return this.post('/api/auth/password/change', input); }
	requestPasswordSetup(turnstileToken?: string) { return this.post('/api/auth/password/setup-request', { turnstileToken }); }
	completeOAuthLink(input: CompleteOAuthLinkRequest) { return this.post('/api/auth/google/link/complete', input); }
	cancelOAuthLink() { return this.post('/api/auth/google/link/cancel', {}); }
	async login(input: LoginRequest) {
		const result = await this.post("/api/auth/login", input);
		this.currentSubscriptionRequest = null;
		this.onIdentityScopeChanged();
		return result;
	}
	async register(input: RegisterRequest) {
		const result = await this.post("/api/auth/register", input) as RegisterResponse;
		this.currentSubscriptionRequest = null;
		this.onIdentityScopeChanged();
		return result;
	}
	async logout() {
		try {
			return await this.post("/api/auth/logout", {});
		} finally {
			this.currentSubscriptionRequest = null;
			this.onIdentityScopeChanged();
		}
	}

	startOAuth(provider: "google", termsAcceptance: { acceptedTerms: true; termsVersion: string }) {
		const query = new URLSearchParams({ mode: 'json' });
		query.set('flow', 'register');
		query.set('acceptedTerms', 'true');
		query.set('termsVersion', termsAcceptance.termsVersion);
		return this.get(`/api/auth/${provider}/start?${query.toString()}`) as Promise<{ provider: "google"; authorizationUrl: string }>;
	}

	entitlements(plan: PlanCode) { return this.get(`/api/entitlements?plan=${encodeURIComponent(plan)}`); }

	getChapter(topicId: string, chapterIndex: number, plan: PlanCode) {
		return this.get(`/api/topics/${encodeURIComponent(topicId)}/chapters/${chapterIndex}?plan=${encodeURIComponent(plan)}`);
	}

	getCoursePageModel(path: string) {
		return this.get(path) as Promise<TopicPageModel | ChapterPageModel | { private: true }>;
	}

	listTopicChapters(slugOrId: string) {
		return this.get(`/api/topics/${encodeURIComponent(slugOrId)}/chapters`) as Promise<TopicChapterListResponse>;
	}

	topicNavigation(slugOrId: string) { return this.get(`/api/topics/${encodeURIComponent(slugOrId)}/navigation`) as Promise<{ topic: PublicIndexResponse["topics"][number] }>; }

	topicCatalog(options: number | TopicCatalogOptions = 20) {
		return this.get(`/api/topics/catalog?${pagedQuery(typeof options === "number" ? { limit: options } : options)}`) as Promise<PublicIndexResponse>;
	}

	topicRecommended(options: number | PagedListOptions = 20) {
		return this.get(`/api/topics/recommended?${pagedQuery(typeof options === "number" ? { limit: options } : options)}`) as Promise<TopicListResponse>;
	}

	topicRecent(options: number | PagedListOptions = 20, signal?: AbortSignal) {
		return this.get(`/api/topics/recent?${pagedQuery(typeof options === "number" ? { limit: options } : options)}`, signal) as Promise<TopicListResponse>;
	}

	topicTrending(options: number | TopicTrendingOptions = 20) {
		return this.get(`/api/topics/trending?${pagedQuery(typeof options === 'number' ? { limit: options } : options)}`) as Promise<PublicIndexResponse & {
			rankingVersion?: string;
			algorithmVersion?: string;
			generatedAt?: string;
			rankingMode?: 'rising' | 'active' | 'popular' | 'new' | 'mixed' | 'empty';
			label?: string;
			returnedTopics?: number;
		}>;
	}

	topicNewest(options: number | TopicNewestOptions = 20) {
		return this.get(`/api/topics/newest?${pagedQuery(typeof options === 'number' ? { limit: options } : options)}`) as Promise<PublicIndexResponse & {
			rankingVersion?: string;
			algorithmVersion?: string;
			generatedAt?: string;
			returnedTopics?: number;
		}>;
	}

	topicDetail(slug: string) {
		return this.get(`/api/topics/${encodeURIComponent(slug)}/detail`) as Promise<{ topic: PublicIndexResponse["topics"][number] }>;
	}

	siteMetrics() { return this.get("/api/site/metrics") as Promise<SiteMetricsResponse>; }

	getCommunityIdea(ideaId: string, signal?: AbortSignal) {
		return this.get(`/api/community/ideas/${encodeURIComponent(ideaId)}`, signal) as Promise<CommunityIdeaDetailResponse>;
	}

	getAiJobDetail(jobId: string, signal?: AbortSignal) {
		return this.get(`/api/ai/jobs/${encodeURIComponent(jobId)}`, signal) as Promise<AiJobDetailResponse>;
	}

	feedbackStats() {
		return this.get("/api/feedback/stats") as Promise<{ received: number | null; resolved: number | null; resolutionRate: number | null; reviewSlaHours: number }>;
	}

	myFeedback() {
		return this.get("/api/feedback/mine") as Promise<{
			feedbacks: Array<{ id: string; category: string; subject: string; status: string; version: number; lastActorRole: 'user' | 'admin'; hasAttachment: boolean; createdAt: string; lastReplyAt: string | null; replyCount: number }>;
		}>;
	}

	feedbackDetail(ticketId: string) {
		return this.get(`/api/feedback/${encodeURIComponent(ticketId)}`) as Promise<{
			ticket: { id: string; category: string; subject: string; details: string; status: string; version: number; lastActorRole: 'user' | 'admin'; hasAttachment: boolean; createdAt: string };
			messages: Array<{ id: string; authorRole: "user" | "admin"; body: string; createdAt: string }>;
		}>;
	}

	feedbackAttachmentDownload(ticketId: string) {
		return `/api/feedback/${encodeURIComponent(ticketId)}/attachment`;
	}

	replyFeedback(ticketId: string, input: { body: string; expectedVersion: number; mutationId: string }) {
		return this.post(`/api/feedback/${encodeURIComponent(ticketId)}/reply`, input);
	}

	communityStats() {
		return this.get("/api/community/stats") as Promise<{ ideasProduced: number | null; avgReviewTime: string | null }>;
	}

	async previewUpgrade(input: UpgradePreviewRequest): Promise<BillingUpgradePreviewResponse> {
		return parseBillingUpgradePreviewResponse(await this.post("/api/billing/upgrade-preview", input));
	}
	createCheckout(input: CheckoutRequest) { return this.post("/api/billing/checkout", input) as Promise<CheckoutResponse>; }

	getCurrentSubscription(options: { force?: boolean } = {}) {
		if (options.force) this.currentSubscriptionRequest = null;
		const now = Date.now();
		if (
			this.currentSubscriptionRequest
			&& (this.currentSubscriptionRequest.settledAt === null || now - this.currentSubscriptionRequest.settledAt < 2_000)
		) return this.currentSubscriptionRequest.promise;
		const promise = this.get("/api/subscription/current") as Promise<CurrentSubscriptionResponse>;
		const request = { promise, settledAt: null as number | null };
		this.currentSubscriptionRequest = request;
		const retainForFreshnessWindow = () => {
			request.settledAt = Date.now();
			setTimeout(() => {
				if (this.currentSubscriptionRequest?.promise === promise) this.currentSubscriptionRequest = null;
			}, 2_000);
		};
		const clearRejectedRequest = () => {
			if (this.currentSubscriptionRequest?.promise === promise) this.currentSubscriptionRequest = null;
		};
		void promise.then(retainForFreshnessWindow, clearRejectedRequest);
		return promise;
	}

	createBillingPortal(subscriptionId?: string) { return this.post("/api/billing/portal", { subscriptionId }); }
	async upgradeSubscription(input: BillingUpgradeRequest) {
		const result = await this.post("/api/billing/upgrade", input);
		this.currentSubscriptionRequest = null;
		return result;
	}
	async setAutoRenew(enabled: boolean, currentPeriodEnd: string, subscriptionId?: string, turnstileToken?: string) {
		const result = await this.post("/api/billing/auto-renew", { enabled, currentPeriodEnd, subscriptionId, turnstileToken });
		this.currentSubscriptionRequest = null;
		return result;
	}
	async audioPlaybackToken(input: PlaybackTokenRequest, signal?: AbortSignal) {
		return this.requestPlaybackToken('/api/media/audio/playback-token', input, 'audio', signal);
	}
	async videoPlaybackToken(input: PlaybackTokenRequest, signal?: AbortSignal) {
		return this.requestPlaybackToken('/api/media/video/playback-token', input, 'video', signal);
	}
	private async requestPlaybackToken(path: string, input: PlaybackTokenRequest, mediaKind: 'audio' | 'video', signal?: AbortSignal) {
		try {
			const response = await this.post(path, withPlaybackAuthorization(input, mediaKind), signal);
			acceptPlaybackAuthorization(response);
			settlePlaybackTokenRequest(input, mediaKind, 'success');
			return response;
		} catch (error) {
			settlePlaybackTokenRequest(input, mediaKind, error instanceof PrototypeApiError ? 'terminal-error' : 'response-lost');
			throw error;
		}
	}
	markAudioPlaybackStarted(playbackLeaseId: string) {
		return this.post('/api/media/audio/playback-started', { playbackLeaseId }) as Promise<{ ok: true; counted: boolean }>;
	}
	heartbeatPlaybackLease(playbackLeaseId: string) {
		return this.post('/api/media/lease/heartbeat', { playbackLeaseId }) as Promise<{ ok: true; expiresInSeconds: number }>;
	}
	releasePlaybackLease(playbackLeaseId: string) {
		return this.postBackground('/api/media/lease/release', { playbackLeaseId }) as Promise<{ ok: true }>;
	}
	promotePlaybackLease(playbackLeaseId: string) {
		return this.post('/api/media/lease/promote', { playbackLeaseId }) as Promise<{ ok: true }>;
	}
	createAiJob(input: AiJobRequest, idempotencyKey?: string) {
		return idempotencyKey
			? this.postWithHeaders('/api/ai/jobs', input, { 'idempotency-key': idempotencyKey })
			: this.post('/api/ai/jobs', input);
	}
}
